import '@ninox/ninox-components/main.css';
import '@ninox/ninox-components/fonts.css';
import './index.css';

import React, { useEffect, useState } from 'react';
import * as ReactDOM from 'react-dom';

import { appHost } from './common/constants';
import { getRedirectUrl } from './utils/util';
import { Credentials, CredentialsPages } from './pages/Credentials';
import { SessionInfo } from './pages/Credentials.types';
import { getGoogleClientIdAsync, trackLogin, trackSignup, trackSignupDetails } from './utils/ga';
import { retrieveGclidField } from './utils/gclid';
import * as hs from './utils/hubspot';
import * as mixpanel from './utils/mixpanel';
import {
    googleLogin,
    login,
    logout,
    requestResetPassword,
    sessionInfo,
    signup,
    updateUserDetails
} from './ums/ums-service';

function getPageInfo() {
    const urlParams = new URLSearchParams(window.location.search);
    let language = window.navigator.language.startsWith('de') ? 'de' : 'en';

    if (window.location.pathname.startsWith('/de')) {
        language = 'de';
    } else if (window.location.pathname.startsWith('/en')) {
        language = 'en';
    }

    const page = window.location.pathname.endsWith('/sign-up') ? 'SignUp' : 'SignIn';

    return {
        language,
        page,
        email: urlParams.get('email'),
        team: urlParams.get('team'),
        redirect: urlParams.get('redirect')
    };
}

function Loader() {
    return (
        <div
            style={{
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: '#fff',
                position: 'absolute',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
            }}
        >
            <div className="credentials-spinner" />
        </div>
    );
}

function CredentialsApp() {
    const pageInfo = getPageInfo();
    // Google client ID for tracking
    const [googleClientId, setGoogleClientId] = useState<number | null>(null);
    const [page, setPage] = useState(pageInfo.page);
    const [language, setLanguage] = useState(pageInfo.language);
    const [googleClickId, setGoogleClickId] = useState<string | undefined>(undefined);
    const [authContext, setAuthContext] = useState<undefined | null | SessionInfo>(undefined);
    const showLoader = googleClientId === null || authContext === undefined;
    const alreadyLoggedIn = authContext !== null && authContext !== undefined;

    if (alreadyLoggedIn) {
        window.location.href = appHost;
    }

    useEffect(() => {
        getGoogleClientIdAsync()
            .then((clientId) => {
                setGoogleClientId(clientId);
            })
            .catch((err) => {
                console.warn('Error while fetching clientId', err);
                setGoogleClientId(123456.3);
            });
    }, []);

    useEffect(() => {
        const handlePopState = () => {
            const newInfo = getPageInfo();

            setPage(newInfo.page);
            setLanguage(newInfo.language);
        };

        window.addEventListener('popstate', handlePopState);

        return () => {
            window.removeEventListener('popstate', handlePopState);
        };
    }, []);

    // Google click id for Google ads
    useEffect(() => {
        setGoogleClickId(retrieveGclidField());
    }, []);

    // Auth context to handle wether to redirect or not
    useEffect(() => {
        sessionInfo()
            .then((user) => setAuthContext(user))
            .catch(() => setAuthContext(null));
    }, []);

    const handlePageChange = (newPage: string) => {
        if (newPage === 'SignIn') {
            history.pushState({}, 'Ninox', `/${language}/sign-in${location.search}`);
        }

        if (newPage === 'SignUp') {
            history.pushState({}, 'Ninox', `/${language}/sign-up${location.search}`);
        }

        return true;
    };

    return showLoader ? (
        <Loader />
    ) : (
        <Credentials
            // Props unnecessary here
            afterLogin={(result) => {
                const redirect = decodeURIComponent(pageInfo.redirect ?? '');
                if (redirect) {
                    window.location.replace(getRedirectUrl(redirect));
                } else if (result && (result.loginSuccessful || result.justSignedUp)) {
                    window.location.href = appHost;
                }
            }}
            canSignup={true}
            email={pageInfo.email}
            forgotPassword={requestResetPassword}
            googleLogin={(isBusiness) => googleLogin({ lang: language, googleClientId, googleClickId, isBusiness })}
            googleTracker={{
                trackLogin,
                trackSignup,
                trackSignupDetails
            }}
            hubspotTracker={hs}
            login={login}
            logout={logout}
            mixPanelTracker={{
                ...mixpanel,
                track(category, action, options) {
                    return mixpanel.track('Credentials', category, action, options);
                }
            }}
            proposedPage={page as CredentialsPages}
            signup={(params) => signup({ ...params, language, noTeam: pageInfo.team === 'false' })}
            updateUserDetails={(userDetails) => updateUserDetails(userDetails)}
            wantsSignup={pageInfo.page === 'SignUp'}
            onPageChange={handlePageChange}
            changeExpiredPassword={() => Promise.resolve(undefined)}
            // Props that are required
            lang={language}
        />
    );
}

ReactDOM.render(React.createElement(CredentialsApp), document.getElementById('root'));
